import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';

import {
	type ConnectorProps,
	ConnectorSources,
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--rovo-expansion/search-page-results-quick-find';

import { SearchPageQuickFind3PBanner } from '../../../components/SearchPageQuickFind3PBanner';

import { type MessageTemplateContext } from '../../../index';

import { messages } from '../../../messages';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

type MessageFrontendProps = MessageTemplateContext & PlacementRenderProps;

/**
 * Snooze durations in days for each dismissal in the cycle.
 * 1st–3rd dismiss → 1 day (same-day cool-off: hidden until the next day).
 * 4th dismiss → 90 days (~3 months), then the dismissal cycle resets.
 */
const SNOOZE_DAYS = [1, 1, 1, 90] as const;

/**
 * Returns the connector after the last dismissed one, or the first connector if none dismissed.
 */
const selectConnectorForBanner = (
	availableConnectors: MessageFrontendProps['availableConnectors'],
	lastDismissed3P: string,
): ConnectorProps | null => {
	const lastIndex = availableConnectors.findIndex((c) => c.connectorId === lastDismissed3P);
	// Start searching from the connector after the last dismissed one
	const candidates = availableConnectors.slice(lastIndex + 1);
	return candidates.find((c) => c.userNeedsOAuth) ?? null;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<MessageFrontendProps>(),
		Component: ({
			dismiss,
			availableConnectors,
			useOAuthHandlers,
			productSource,
			lastDismissed3P,
			dismissalCount,
			fullConnectorVariant,
			smartLinkVariant,
			onMessageViewed,
			...rest
		}: MessageFrontendProps) => {
			const { messageInstanceViewed } = useMessageInstanceEvent();
			const { onSnoozed } = usePostOfficeUserInteractions();
			const { formatMessage } = useIntl();
			const connector = selectConnectorForBanner(availableConnectors, lastDismissed3P);

			if (!connector) {
				return null;
			}

			const { connectorId, connectorMode } = connector;
			const variant =
				connectorMode === ConnectorSources.Smartlinks ? smartLinkVariant : fullConnectorVariant;

			// Ref keeps the callback reference stable so the effect only re-runs when the
			// actual data values change, not when onMessageViewed gets a new reference.
			// The effect calls onMessageViewed with a trackImpression function, letting the
			// caller decide if/when to trigger messageInstanceViewed.
			const onMessageViewedRef = useRef(onMessageViewed);
			onMessageViewedRef.current = onMessageViewed;
			useEffect(() => {
				if (!onMessageViewedRef.current || !messageInstanceViewed) {
					return;
				}
				onMessageViewedRef.current(() => {
					void messageInstanceViewed({
						productSource,
						connectorSource: connectorMode,
						connectorId,
						variant,
					});
				});
			}, [messageInstanceViewed, connectorId, connectorMode, productSource, variant]);

			const { onAuthorize } = useOAuthHandlers({
				product: connector.connectorProductKey,
				connectorSource: connector.connectorMode,
				successFlagTitle: formatMessage(messages.authorizationSuccess, {
					product: connector.connectorName,
				}),
				failFlagTitle: formatMessage(messages.authorizationFail, {
					product: connector.connectorName,
				}),
				outboundAuthUrl: connector.oAuthUrl,
				isOnSearchDialog: true,
				extraAttributes: {
					connectorId,
					variant,
				},
			});

			const handleDismiss = useCallback(() => {
				if (!connector) {
					return;
				}

				const snoozeDuration = SNOOZE_DAYS[dismissalCount] ?? SNOOZE_DAYS[0];

				void onSnoozed({
					interactionIdentifier: 'dismiss-3p-banner',
					durationDays: snoozeDuration,
					additionalMetadata: {
						actionReason: 'dismiss',
						dismissedConnectorId: connector.connectorId,
						dismissedVariant: variant,
						variant,
						productSource,
						connectorSource: connector.connectorMode,
					},
				});

				dismiss();
			}, [onSnoozed, dismiss, dismissalCount, connector, productSource, variant]);

			return (
				<ManualMessageViewedEvent>
					<SearchPageQuickFind3PBanner
						context={{
							productSource,
							onAuthorize,
							variant,
							connector,
							teamConnectAppPercentage: rest.teamConnectAppPercentage,
						}}
						onDismiss={handleDismiss}
					/>
				</ManualMessageViewedEvent>
			);
		},
	},
});
